import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";

// uReserve-branded replacement for browser-native `window.confirm`.
// Two tones (neutral = navy header · danger = coral header) mapped to
// the same modal chrome used by NuclearDeleteModal and MembershipsDrawer.
// Async onConfirm supported — the button shows a spinner and stays
// disabled until the returned promise resolves; errors bubble to the
// body as a coral banner.
//
// 2026-02-16 (Dave) — replaces `window.confirm()` calls scattered
// across UsersScreen / ElementsScreen / SupportScreen so no confirmation
// flow leaks the browser's default styling.
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "neutral",           // "neutral" | "danger"
  icon = null,                // lucide-react icon element, optional
  hideCancel = false,         // one-button "OK" mode for alert-style dialogs
  confirmDisabled = false,
  onConfirm,
  onCancel,
  testid = "confirm-dialog",
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reset transient state whenever the dialog is re-opened for a new
  // question. Without this, a prior "failed" state (busy=true or an
  // error banner) would flash before the fresh dialog rendered.
  useEffect(() => {
    if (open) {
      setBusy(false);
      setErr("");
    }
  }, [open]);

  // Esc key closes; Enter triggers confirm — matches browser-native
  // confirm() behavior so keyboard flows aren't a regression.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
      if (e.key === "Enter" && !busy && !confirmDisabled) handleConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, confirmDisabled]);

  if (!open) return null;

  const isDanger = tone === "danger";
  const borderClass = isDanger ? "border-coral" : "border-navy";
  const headerBg = isDanger ? "bg-coral" : "bg-navy";

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      await onConfirm?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Action failed.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(15,42,68,0.55)" }}
      onClick={busy ? undefined : onCancel}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className={`bg-white border-2 ${borderClass} shadow-2xl w-[440px] max-w-[92vw] rounded-[8px]`}
        style={{ borderRadius: 8, overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className={`flex items-center justify-between px-4 py-3 ${headerBg} text-white`}>
          <div className="flex items-center gap-2">
            {icon || (isDanger ? <AlertTriangle size={16} /> : null)}
            <span className="font-bold text-[13px] tracking-wide uppercase">
              {title}
            </span>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="hover:opacity-80 disabled:opacity-40"
            data-testid={`${testid}-close`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-4 py-4">
          <div className="text-[13px] text-ink leading-relaxed">
            {message}
          </div>
          {err && (
            <div
              className="mt-3 text-[11.5px] text-coral border border-coral px-2 py-1.5 rounded-[3px] bg-[#FEF5F2]"
              data-testid={`${testid}-error`}
            >
              {err}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 bg-navy-wash border-t border-rule">
          {!hideCancel && (
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-3 py-1.5 text-[12px] text-slate hover:text-navy disabled:opacity-40"
              data-testid={`${testid}-cancel`}
            >
              {cancelLabel}
            </button>
          )}
          <button
            onClick={handleConfirm}
            disabled={busy || confirmDisabled}
            className="px-3 py-1.5 text-[12px] font-bold text-white rounded-[3px] flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: isDanger ? "#EA6A47" : "#1C4E80" }}
            data-testid={`${testid}-confirm`}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
